import React, { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Plus, Minus } from 'lucide-react';
import Select from 'react-select';
import LexicalEditor from './LexicalEditor';
import { loadRuleTargetTypes } from '../../rules/ruleService';
import { showDangerToast } from '../../validation/ToastContainer';
import SelectV2 from '../../view/SelectV2';

const MAX_MENU_HEIGHT = 240;

const ThemeSectionCard = ({
    section,
    index,
    totalSections,
    onUpdate,
    onDelete,
    onMove,
    sectionTypes
}) => {
    const [sectionName, setSectionName] = useState(section.sectionName || '');
    const [config, setConfig] = useState({});
    const [contentHtml, setContentHtml] = useState(section.contentHtml || '');
    const [collapsed, setCollapsed] = useState(true);
    const [targetTypeOptions, setTargetTypeOptions] = useState({});
    const [targetTypeOptionsLoading, setTargetTypeOptionsLoading] = useState(false);
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const tableViolationSelectMenuProps = {
        menuPlacement: 'auto',
        menuPosition: 'fixed',
        maxMenuHeight: MAX_MENU_HEIGHT,
        minMenuHeight: MAX_MENU_HEIGHT,
        menuPortalTarget,
        menuShouldScrollIntoView: false
    };

    // Parse config from JSON
    useEffect(() => {
        try {
            const parsed = section.configJson ? JSON.parse(section.configJson) : {};
            // Backward compat: migrate old flat filters to filterGroups
            if (section.sectionType === 'TABLE_VIOLATIONS' && !parsed.filterGroups) {
                parsed.filterGroups = [{
                    targetType: '',
                    targetTypeValue: '',
                    severityFilter: parsed.severityFilter || 'ALL',
                    validFilter: parsed.validFilter || 'ALL'
                }];
            }
            setConfig(parsed);
        } catch (e) {
            setConfig({});
        }
    }, [section.configJson, section.sectionType]);

    // Load target type options for sections that use violation/rule/device filters
    useEffect(() => {
        if (['TABLE_VIOLATIONS', 'CHART_MSB', 'CHART_COMPLIANCE', 'CHART_DEVICE_STATUS', 'CHART_RISK'].includes(section.sectionType) && !collapsed) {
            let cancelled = false;
            setTargetTypeOptionsLoading(true);
            loadRuleTargetTypes().then(data => {
                if (!cancelled) {
                    setTargetTypeOptions(data || {});
                }
            }).catch(err => console.error('Error loading target types:', err))
                .finally(() => {
                    if (!cancelled) {
                        setTargetTypeOptionsLoading(false);
                    }
                });

            return () => {
                cancelled = true;
            };
        }
    }, [section.sectionType, collapsed]);

    // Update section name
    const handleNameChange = (value) => {
        setSectionName(value);
        onUpdate({ sectionName: value });
    };

    // Update config
    const handleConfigChange = (key, value) => {
        const newConfig = { ...config, [key]: value };
        setConfig(newConfig);
        onUpdate({ configJson: JSON.stringify(newConfig) });
    };

    // Update HTML content locally and sync to parent (Auto-save)
    const handleHtmlChange = (value) => {
        setContentHtml(value);
        onUpdate({ contentHtml: value });
    };

    // Helper: Get available visualization types for a chart
    const getAvailableVisualizationTypes = (sectionType) => {
        switch (sectionType) {
            case 'CHART_COMPLIANCE':
                return [
                    { value: 'line', label: 'Line Chart' },
                    { value: 'bar', label: 'Bar Chart' }
                ];
            case 'CHART_MSB':
                return [
                    { value: 'bar', label: 'Bar Chart' },
                    { value: 'line', label: 'Line Chart' }
                ];
            case 'CHART_VIOLATIONS':
                return [
                    { value: 'bar', label: 'Bar Chart' },
                    { value: 'line', label: 'Line Chart' }
                ];
            case 'CHART_DEVICE_STATUS':
                return [
                    { value: 'pie', label: 'Pie Chart' },
                    { value: 'doughnut', label: 'Doughnut Chart' }
                ];
            case 'CHART_RISK':
                return [
                    { value: 'doughnut', label: 'Doughnut Chart' },
                    { value: 'pie', label: 'Pie Chart' }
                ];
            default:
                return [];
        }
    };

    // Helper: Get default visualization type for a chart
    const getDefaultVisualizationType = (sectionType) => {
        switch (sectionType) {
            case 'CHART_COMPLIANCE':
                return 'line';
            case 'CHART_MSB':
                return 'bar';
            case 'CHART_VIOLATIONS':
                return 'bar';
            case 'CHART_DEVICE_STATUS':
                return 'pie';
            case 'CHART_RISK':
                return 'doughnut';
            default:
                return null;
        }
    };

    // Helper: Get chart preview image filename based on section type and visualization type
    const getChartPreviewImage = (sectionType, vizType) => {
        const vizTypeResolved = vizType || getDefaultVisualizationType(sectionType);
        if (!vizTypeResolved) return null;

        const imageMap = {
            'CHART_COMPLIANCE': {
                'line': 'compliance_line.png',
                'bar': 'compliance_bar.png'
            },
            'CHART_MSB': {
                'bar': 'msb_bar.png',
                'line': 'msb_line.png'
            },
            'CHART_DEVICE_STATUS': {
                'pie': 'device_status_pie.png',
                'doughnut': 'device_status_doughnut.png'
            },
            'CHART_RISK': {
                'doughnut': 'risk_score_doughnut.png',
                'pie': 'risk_score_pie.png'
            }
        };

        return imageMap[sectionType]?.[vizTypeResolved] || null;
    };

    // Render chart preview image for chart-type sections
    const renderChartPreview = () => {
        const type = sectionTypes[section.sectionType];
        if (!type || type.category !== 'chart') return null;

        const vizType = config.chartVisualizationType || getDefaultVisualizationType(section.sectionType);
        const imageFile = getChartPreviewImage(section.sectionType, vizType);
        if (!imageFile) return null;

        return (
            <div className="chart-preview-container">
                <div className="chart-preview-header">
                    <span className="chart-preview-label">Preview</span>
                    <span className="chart-preview-badge">Sample Data</span>
                </div>
                <img
                    src={`${process.env.PUBLIC_URL}/chart-previews/${imageFile}`}
                    alt={`${type.label} preview`}
                    className="chart-preview-img"
                />
            </div>
        );
    };

    // Get category badge style
    const getCategoryBadge = () => {
        const type = sectionTypes[section.sectionType];
        if (!type) return null;
        const badgeLabel = type.label
            .replace(/\s+([^\s]+)$/, '-$1')
            .toUpperCase();

        const categoryClass = `section-type-badge--${type.category || 'custom'}`;

        return (
            <span className={`section-type-badge ${categoryClass}`}>
                {badgeLabel}
            </span>
        );
    };

    const getSectionIcon = () => {
        const type = sectionTypes[section.sectionType];
        if (!type?.icon) return null;

        const IconComponent = type.icon;

        const categoryClass = `section-type-icon--${type.category || 'custom'}`;

        return (
            <span className={`section-type-icon ${categoryClass}`} aria-hidden="true">
                <IconComponent size={16} strokeWidth={2.1} />
            </span>
        );
    };

    const getReportFilterTargetTypeList = () => ([
        { value: '', label: 'Select Property' },
        { value: 'Location', label: 'Location' },
        { value: 'Compliance', label: 'Compliance' },
        { value: 'Sector', label: 'Sector' },
        { value: 'MSB', label: 'MSB' },
        { value: 'Group', label: 'Group' },
        { value: 'Tags', label: 'Tags' },
        { value: 'Severity', label: 'Severity' },
        { value: 'Valid', label: 'Valid Status' },
        { value: 'RuleName', label: 'Rule Name' },
        { value: 'RuleDescription', label: 'Description' }
    ]);

    const getReportFilterTargetTypeItems = (targetType) => {
        if (!targetType || !targetTypeOptions) return [];
        switch (targetType) {
            case 'Location': return (targetTypeOptions.locations || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'Compliance': return (targetTypeOptions.compliance || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'Sector': return (targetTypeOptions.sectors || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'MSB': return (targetTypeOptions.msbList || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'Group': return (targetTypeOptions.groups || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'Tags': return (targetTypeOptions.tags || []).map(i => ({ value: i.name || i.id, label: i.name }));
            case 'Severity': return [{ value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }];
            case 'Valid': return [{ value: 'PASSED', label: 'Passed' }, { value: 'FAIL', label: 'Fail' }, { value: 'UNKNOWN', label: 'Unknown' }];
            default: return [];
        }
    };

    const shouldShowTargetTypeLoader = (targetType) => (
        targetTypeOptionsLoading && Boolean(targetType)
    );

    const shouldShowTargetTypeEmptyState = (targetType, targetTypeItems) => (
        Boolean(targetType) && !targetTypeOptionsLoading && targetTypeItems.length === 0
    );

    const supportsMultipleFilterValues = (targetType) => (
        ['Location', 'Compliance', 'Sector', 'MSB', 'Group', 'Tags'].includes(targetType)
    );

    const getSelectedFilterOptions = (targetTypeItems, targetTypeValue) => {
        const selectedValues = (targetTypeValue || '').split(',').filter(Boolean);
        return targetTypeItems.filter(item => selectedValues.includes(String(item.value)));
    };

    const getFilterValueFromSelectOption = (option) => (
        Array.isArray(option)
            ? option.map(item => item.value).join(',')
            : option?.value || ''
    );

    const renderReportFilterGroups = (filterGroups = [], allowedTargetTypes = null) => {
        const groups = filterGroups.length ? filterGroups : [{ targetType: '', targetTypeValue: '' }];
        const targetTypeList = getReportFilterTargetTypeList().filter(item =>
            item.value === '' || !allowedTargetTypes || allowedTargetTypes.includes(item.value)
        );
        const requiresExplicitValue = ['RuleName', 'RuleDescription', 'Severity', 'Valid'];

        const updateGroups = (newGroups) => handleConfigChange('filterGroups', newGroups);

        const handleFilterGroupChange = (groupIndex, field, value) => {
            const newGroups = [...groups];
            newGroups[groupIndex] = { ...newGroups[groupIndex], [field]: value };
            if (field === 'targetType') {
                if (value === 'Severity') {
                    newGroups[groupIndex].targetTypeValue = 'LOW,MEDIUM,HIGH';
                } else if (value === 'Valid') {
                    newGroups[groupIndex].targetTypeValue = 'PASSED,FAIL,UNKNOWN';
                } else {
                    newGroups[groupIndex].targetTypeValue = '';
                }
            }
            updateGroups(newGroups);
        };

        const handleCheckboxToggle = (groupIndex, currentValue, toggledValue) => {
            const values = currentValue ? currentValue.split(',').filter(Boolean) : [];
            const idx = values.indexOf(toggledValue);
            if (idx >= 0) {
                values.splice(idx, 1);
            } else {
                values.push(toggledValue);
            }
            handleFilterGroupChange(groupIndex, 'targetTypeValue', values.join(','));
        };

        const validateFilters = () => {
            for (let i = 0; i < groups.length; i++) {
                const g = groups[i];
                if (!g.targetType && groups.length > 1) {
                    showDangerToast(`Filter row ${i + 1}: Please select a filter property or remove this row.`, 'Validation Error');
                    return false;
                }
                if (requiresExplicitValue.includes(g.targetType) && !g.targetTypeValue) {
                    showDangerToast(`Filter row ${i + 1}: Please select or enter a value for "${targetTypeList.find(t => t.value === g.targetType)?.label}".`, 'Validation Error');
                    return false;
                }
            }
            return true;
        };

        const addFilterGroup = () => {
            if (!validateFilters()) return;
            updateGroups([...groups, { targetType: '', targetTypeValue: '' }]);
        };

        const removeFilterGroup = (groupIndex) => {
            const nextGroups = groups.filter((_, i) => i !== groupIndex);
            updateGroups(nextGroups.length ? nextGroups : []);
        };

        return (
            <div className="section-config-item">
                <label>Filter Groups</label>
                <div className="filter-groups-container">
                    {groups.map((group, groupIndex) => (
                        <div key={groupIndex} className="filter-group-row">
                            <div className="filter-group-fields">
                                <div className="filter-group-field fixed-property">
                                    <span className="filter-group-field-label">Filter Property</span>
                                    <Select
                                        inputId={`theme-filter-property-${section.clientId || section.id || index}-${groupIndex}`}
                                        instanceId={`theme-filter-property-${section.clientId || section.id || index}-${groupIndex}`}
                                        className="oct_selectsearch-v2 theme-section-select"
                                        classNamePrefix="oct-selectsearchsub-v2"
                                        value={targetTypeList.find(tt => tt.value === (group.targetType || ''))}
                                        onChange={(option) => handleFilterGroupChange(groupIndex, 'targetType', option.value)}
                                        options={targetTypeList}
                                        isOptionDisabled={(option) =>
                                            option.value !== '' &&
                                            !['RuleName', 'RuleDescription'].includes(option.value) &&
                                            groups.some((g, i) => i !== groupIndex && g.targetType === option.value)
                                        }
                                        isSearchable={true}
                                        {...tableViolationSelectMenuProps}
                                        placeholder="Select Property..."
                                    />
                                </div>

                                {group.targetType && ['RuleName', 'RuleDescription'].includes(group.targetType) ? (
                                    <div className="filter-group-field">
                                        <span className="filter-group-field-label">Value</span>
                                        <input
                                            className={`filter-group-input ${group.targetType && !group.targetTypeValue ? 'has-error' : ''}`}
                                            type="text"
                                            placeholder={`Enter ${targetTypeList.find(t => t.value === group.targetType)?.label}...`}
                                            value={group.targetTypeValue || ''}
                                            onChange={(e) => handleFilterGroupChange(groupIndex, 'targetTypeValue', e.target.value)}
                                        />
                                    </div>
                                ) : group.targetType && ['Severity', 'Valid'].includes(group.targetType) ? (
                                    <div className="filter-group-field">
                                        <span className="filter-group-field-label">Value</span>
                                        <div className={`filter-group-checkbox-group columns-checkbox-group columns-checkbox-group--filter ${group.targetType && !group.targetTypeValue ? 'has-error' : ''}`}>
                                            {getReportFilterTargetTypeItems(group.targetType).map(item => {
                                                const selectedValues = (group.targetTypeValue || '').split(',').filter(Boolean);
                                                const isChecked = selectedValues.includes(item.value);
                                                return (
                                                    <label key={item.value} className="column-checkbox filter-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleCheckboxToggle(groupIndex, group.targetTypeValue || '', item.value)}
                                                        />
                                                        <span>{item.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : group.targetType ? (
                                    (() => {
                                        const targetTypeItems = getReportFilterTargetTypeItems(group.targetType);
                                        const isTargetTypeLoading = shouldShowTargetTypeLoader(group.targetType);
                                        const hasNoTargetTypeItems = shouldShowTargetTypeEmptyState(group.targetType, targetTypeItems);
                                        const isMultiValueFilter = supportsMultipleFilterValues(group.targetType);
                                        const selectedValue = isMultiValueFilter
                                            ? getSelectedFilterOptions(targetTypeItems, group.targetTypeValue)
                                            : targetTypeItems.find(i => i.value === (group.targetTypeValue || '')) || { value: '', label: 'All' };
                                        const selectOptions = isMultiValueFilter
                                            ? targetTypeItems
                                            : [{ value: '', label: 'All' }, ...targetTypeItems];

                                        return (
                                    <div className="filter-group-field">
                                        <span className="filter-group-field-label">Value</span>
                                        <Select
                                            inputId={`theme-filter-value-${section.clientId || section.id || index}-${groupIndex}`}
                                            instanceId={`theme-filter-value-${section.clientId || section.id || index}-${groupIndex}`}
                                            className={`oct_selectsearch-v2 theme-section-select ${isMultiValueFilter ? 'theme-section-select--multi' : ''}`}
                                            classNamePrefix="oct-selectsearchsub-v2"
                                            value={isTargetTypeLoading || hasNoTargetTypeItems ? null : selectedValue}
                                            onChange={(option) => handleFilterGroupChange(groupIndex, 'targetTypeValue', getFilterValueFromSelectOption(option))}
                                            options={isTargetTypeLoading || hasNoTargetTypeItems ? [] : selectOptions}
                                            isMulti={isMultiValueFilter}
                                            isLoading={isTargetTypeLoading}
                                            isSearchable={true}
                                            {...tableViolationSelectMenuProps}
                                            placeholder={isTargetTypeLoading ? 'Loading values...' : hasNoTargetTypeItems ? 'No values available' : 'Select Value...'}
                                            loadingMessage={() => 'Loading values...'}
                                            noOptionsMessage={() => hasNoTargetTypeItems ? 'No values available' : 'No options'}
                                        />
                                    </div>
                                        );
                                    })()
                                ) : (
                                    <div className="filter-group-field">
                                        <span className="filter-group-field-label">Value</span>
                                        <Select
                                            inputId={`theme-filter-value-disabled-${section.clientId || section.id || index}-${groupIndex}`}
                                            instanceId={`theme-filter-value-disabled-${section.clientId || section.id || index}-${groupIndex}`}
                                            className="oct_selectsearch-v2 theme-section-select"
                                            classNamePrefix="oct-selectsearchsub-v2"
                                            isDisabled={true}
                                            isSearchable={false}
                                            placeholder="Select a property first"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="filter-group-actions">
                                {groupIndex === groups.length - 1 && (
                                    <button
                                        type="button"
                                        className="filter-group-btn-add"
                                        onClick={addFilterGroup}
                                        title="Add Filter Group"
                                    >
                                        <Plus size={14} />
                                    </button>
                                )}
                                {(groups.length > 1 || group.targetType) && (
                                    <button
                                        type="button"
                                        className="filter-group-btn-remove"
                                        onClick={() => removeFilterGroup(groupIndex)}
                                        title="Remove Filter Group"
                                    >
                                        <Minus size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render config fields based on section type
    const renderConfigFields = () => {
        switch (section.sectionType) {
            case 'CHART_COMPLIANCE':
                // Device is selected when creating the report, but chart type can be customized
                const chartTypes1 = getAvailableVisualizationTypes(section.sectionType);
                const currentType1 = config.chartVisualizationType || getDefaultVisualizationType(section.sectionType);

                return (
                    <div className="section-config-column">
                        <div className="section-config-item">
                            <span className="section-config-info">
                                Device filter is selected when creating the report
                            </span>
                        </div>
                        <div className="section-config-item">
                            <label>Chart Visualization Type</label>
                            <SelectV2
                                inputId={`theme-chart-visualization-${section.clientId || section.id || index}`}
                                className="theme-section-select"
                                value={currentType1}
                                onChange={(value) => handleConfigChange('chartVisualizationType', value)}
                                options={chartTypes1}
                            />
                        </div>
                        {renderReportFilterGroups(config.filterGroups || [], ['Location', 'Compliance', 'Sector', 'MSB', 'Group', 'Tags', 'Severity', 'Valid', 'RuleName', 'RuleDescription'])}
                    </div>
                );

            case 'CHART_MSB':
                const chartTypesMsb = getAvailableVisualizationTypes(section.sectionType);
                const currentTypeMsb = config.chartVisualizationType || getDefaultVisualizationType(section.sectionType);

                return (
                    <div className="section-config-column">
                        <div className="section-config-item">
                            <span className="section-config-info">
                                Device filter is selected when creating the report
                            </span>
                        </div>
                        <div className="section-config-item">
                            <label>Chart Visualization Type</label>
                            <SelectV2
                                inputId={`theme-chart-visualization-${section.clientId || section.id || index}`}
                                className="theme-section-select"
                                value={currentTypeMsb}
                                onChange={(value) => handleConfigChange('chartVisualizationType', value)}
                                options={chartTypesMsb}
                            />
                        </div>
                        {renderReportFilterGroups(config.filterGroups || [], ['MSB', 'Group', 'Tags'])}
                    </div>
                );

            case 'CHART_DEVICE_STATUS':
                const chartTypes2 = getAvailableVisualizationTypes(section.sectionType);
                const currentType2 = config.chartVisualizationType || getDefaultVisualizationType(section.sectionType);

                const statusOptions = [
                    { value: 'CONNECTED', label: 'Connected' },
                    { value: 'DISCONNECTED', label: 'Disconnected' },
                    { value: 'UNKNOWN', label: 'Unknown' }
                ];

                const selectedStatusValues = (() => {
                    const sf = config.statusFilter;
                    if (!sf || sf === 'ALL') return ['CONNECTED', 'DISCONNECTED', 'UNKNOWN'];
                    if (Array.isArray(sf)) return sf.map(s => String(s).trim().toUpperCase()).filter(Boolean);
                    return String(sf).split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
                })();

                const handleStatusToggle = (value) => {
                    const values = selectedStatusValues.includes(value)
                        ? selectedStatusValues.filter(v => v !== value)
                        : [...selectedStatusValues, value];
                    handleConfigChange('statusFilter', values.length > 0 ? values : 'ALL');
                };

                return (
                    <div className="section-config-column">
                        <div className="filter-group-row">
                            <div className="filter-group-fields">
                                <div className="filter-group-field fixed-property">
                                    <span className="filter-group-field-label">Chart Visualization Type</span>
                                    <SelectV2
                                        inputId={`theme-device-status-visualization-${section.clientId || section.id || index}`}
                                        className="theme-section-select"
                                        value={currentType2}
                                        onChange={(value) => handleConfigChange('chartVisualizationType', value)}
                                        options={chartTypes2}
                                    />
                                </div>
                                <div className="filter-group-field">
                                    <span className="filter-group-field-label">Status Filter</span>
                                    <div className="filter-group-checkbox-group columns-checkbox-group columns-checkbox-group--filter">
                                        {statusOptions.map(option => (
                                            <label key={option.value} className="column-checkbox filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStatusValues.includes(option.value)}
                                                    onChange={() => handleStatusToggle(option.value)}
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {renderReportFilterGroups(config.filterGroups || [], ['MSB', 'Group', 'Tags'])}
                    </div>
                );

            case 'CHART_RISK':
                const chartTypes3 = getAvailableVisualizationTypes(section.sectionType);
                const currentType3 = config.chartVisualizationType || getDefaultVisualizationType(section.sectionType);

                const severityOptions = [
                    { value: 'LOW', label: 'Low' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HIGH', label: 'High' }
                ];

                const selectedSeverityValues = (() => {
                    const sf = config.severityFilter;
                    if (!sf || sf === 'ALL') return ['LOW', 'MEDIUM', 'HIGH'];
                    if (Array.isArray(sf)) return sf.map(s => String(s).trim().toUpperCase()).filter(Boolean);
                    return String(sf).split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
                })();

                const handleSeverityToggle = (value) => {
                    const values = selectedSeverityValues.includes(value)
                        ? selectedSeverityValues.filter(v => v !== value)
                        : [...selectedSeverityValues, value];
                    handleConfigChange('severityFilter', values.length > 0 ? values : 'ALL');
                };

                return (
                    <div className="section-config-column">
                        <div className="filter-group-row">
                            <div className="filter-group-fields">
                                <div className="filter-group-field fixed-property">
                                    <span className="filter-group-field-label">Chart Visualization Type</span>
                                    <SelectV2
                                        inputId={`theme-risk-visualization-${section.clientId || section.id || index}`}
                                        className="theme-section-select"
                                        value={currentType3}
                                        onChange={(value) => handleConfigChange('chartVisualizationType', value)}
                                        options={chartTypes3}
                                    />
                                </div>
                                <div className="filter-group-field">
                                    <span className="filter-group-field-label">Severity Filter</span>
                                    <div className="filter-group-checkbox-group columns-checkbox-group columns-checkbox-group--filter">
                                        {severityOptions.map(option => (
                                            <label key={option.value} className="column-checkbox filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSeverityValues.includes(option.value)}
                                                    onChange={() => handleSeverityToggle(option.value)}
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {renderReportFilterGroups(config.filterGroups || [], ['MSB', 'Group', 'Tags'])}
                    </div>
                );

            case 'TABLE_VIOLATIONS':
                const availableColumns = [
                    { value: 'DATE', label: 'Date' },
                    { value: 'RULE_NAME', label: 'Rule Name' },
                    { value: 'DESCRIPTION', label: 'Description' },
                    { value: 'USER', label: 'User' },
                    { value: 'SEVERITY', label: 'Severity' },
                    { value: 'VALID', label: 'Valid' },
                    { value: 'SECTION_NAME', label: 'Section Name' },
                    { value: 'SUBSECTION_NAME', label: 'Subsection Name' }
                ];
                const selectedColumns = config.columns || ['RULE_NAME', 'DESCRIPTION', 'USER', 'SEVERITY', 'VALID'];

                const handleColumnToggle = (columnValue) => {
                    let newColumns;
                    if (selectedColumns.includes(columnValue)) {
                        newColumns = selectedColumns.filter(c => c !== columnValue);
                        if (newColumns.length === 0) return;
                    } else {
                        newColumns = [...selectedColumns, columnValue];
                    }
                    handleConfigChange('columns', newColumns);
                };

                const filterGroups = config.filterGroups || [
                    { targetType: '', targetTypeValue: '', ruleNameFilter: '', ruleDescriptionFilter: '', severityFilter: 'ALL', validFilter: 'ALL' }
                ];

                const targetTypeList = [
                    { value: '', label: 'Select Property' },
                    { value: 'Location', label: 'Location' },
                    { value: 'Compliance', label: 'Compliance' },
                    { value: 'Sector', label: 'Sector' },
                    { value: 'MSB', label: 'MSB' },
                    { value: 'Group', label: 'Group' },
                    { value: 'Tags', label: 'Tags' },
                    { value: 'Severity', label: 'Severity' },
                    { value: 'Valid', label: 'Valid Status' },
                    { value: 'RuleName', label: 'Rule Name' },
                    { value: 'RuleDescription', label: 'Description' }
                ];

                const getTargetTypeItems = (targetType) => {
                    if (!targetType || !targetTypeOptions) return [];
                    switch (targetType) {
                        case 'Location': return (targetTypeOptions.locations || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'Compliance': return (targetTypeOptions.compliance || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'Sector': return (targetTypeOptions.sectors || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'MSB': return (targetTypeOptions.msbList || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'Group': return (targetTypeOptions.groups || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'Tags': return (targetTypeOptions.tags || []).map(i => ({ value: i.name || i.id, label: i.name }));
                        case 'Severity': return [{ value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }];
                        case 'Valid': return [{ value: 'PASSED', label: 'Passed' }, { value: 'FAIL', label: 'Fail' }, { value: 'UNKNOWN', label: 'Unknown' }];
                        default: return [];
                    }
                };

                const handleFilterGroupChange = (groupIndex, field, value) => {
                    const newGroups = [...filterGroups];
                    newGroups[groupIndex] = { ...newGroups[groupIndex], [field]: value };
                    // Reset targetTypeValue when targetType changes
                    if (field === 'targetType') {
                        // For Severity/Valid, pre-select all options by default
                        if (value === 'Severity') {
                            newGroups[groupIndex].targetTypeValue = 'LOW,MEDIUM,HIGH';
                        } else if (value === 'Valid') {
                            newGroups[groupIndex].targetTypeValue = 'PASSED,FAIL,UNKNOWN';
                        } else {
                            newGroups[groupIndex].targetTypeValue = '';
                        }
                    }
                    handleConfigChange('filterGroups', newGroups);
                };

                // Toggle a checkbox value in a comma-separated string
                const handleCheckboxToggle = (groupIndex, currentValue, toggledValue) => {
                    const values = currentValue ? currentValue.split(',').filter(Boolean) : [];
                    const idx = values.indexOf(toggledValue);
                    if (idx >= 0) {
                        values.splice(idx, 1);
                    } else {
                        values.push(toggledValue);
                    }
                    handleFilterGroupChange(groupIndex, 'targetTypeValue', values.join(','));
                };

                // Properties that require an explicit value (not "All")
                const requiresExplicitValue = ['RuleName', 'RuleDescription', 'Severity', 'Valid'];

                // Validate filter groups before adding a new one
                const validateFilters = () => {
                    for (let i = 0; i < filterGroups.length; i++) {
                        const g = filterGroups[i];
                        // Error if property is still "Select Property"
                        if (!g.targetType) {
                            showDangerToast(`Filter row ${i + 1}: Please select a filter property or remove this row.`, 'Validation Error');
                            return false;
                        }
                        // Only error on empty value for types that require explicit selection
                        if (requiresExplicitValue.includes(g.targetType) && !g.targetTypeValue) {
                            showDangerToast(`Filter row ${i + 1}: Please select or enter a value for "${targetTypeList.find(t => t.value === g.targetType)?.label}".`, 'Validation Error');
                            return false;
                        }
                    }
                    return true;
                };

                const addFilterGroup = () => {
                    if (!validateFilters()) return;
                    const newGroups = [...filterGroups, { targetType: '', targetTypeValue: '' }];
                    handleConfigChange('filterGroups', newGroups);
                };

                const removeFilterGroup = (groupIndex) => {
                    if (filterGroups.length <= 1) return;
                    const newGroups = filterGroups.filter((_, i) => i !== groupIndex);
                    handleConfigChange('filterGroups', newGroups);
                };

                return (
                    <div className="section-config-column">
                        {/* Columns Selection */}
                        <div className="section-config-item">
                            <label>Select Columns</label>
                            <div className="columns-checkbox-group">
                                {availableColumns.map(col => (
                                    <label key={col.value} className="column-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(col.value)}
                                            onChange={() => handleColumnToggle(col.value)}
                                        />
                                        <span>{col.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Filter Groups */}
                        <div className="section-config-item">
                            <label>Filter Groups</label>
                            <div className="filter-groups-container">
                                {filterGroups.map((group, groupIndex) => (
                                    <div key={groupIndex} className="filter-group-row">
                                        <div className="filter-group-fields">
                                            {/* Property Selector */}
                                            <div className="filter-group-field fixed-property">
                                                <span className="filter-group-field-label">Filter Property</span>
                                                <Select
                                                    inputId={`theme-filter-property-${section.clientId || section.id || index}-${groupIndex}`}
                                                    instanceId={`theme-filter-property-${section.clientId || section.id || index}-${groupIndex}`}
                                                    className="oct_selectsearch-v2 theme-section-select"
                                                    classNamePrefix="oct-selectsearchsub-v2"
                                                    value={targetTypeList.find(tt => tt.value === (group.targetType || ''))}
                                                    onChange={(option) => handleFilterGroupChange(groupIndex, 'targetType', option.value)}
                                                    options={targetTypeList}
                                                    isOptionDisabled={(option) =>
                                                        option.value !== '' &&
                                                        !['RuleName', 'RuleDescription'].includes(option.value) &&
                                                        filterGroups.some((g, i) => i !== groupIndex && g.targetType === option.value)
                                                    }
                                                    isSearchable={true}
                                                    {...tableViolationSelectMenuProps}
                                                    placeholder="Select Property..."
                                                />
                                            </div>

                                            {/* Value Selector (Dynamic based on property) */}
                                            {group.targetType && ['RuleName', 'RuleDescription'].includes(group.targetType) ? (
                                                <div className="filter-group-field">
                                                    <span className="filter-group-field-label">Value</span>
                                                    <input
                                                        className={`filter-group-input ${group.targetType && !group.targetTypeValue ? 'has-error' : ''}`}
                                                        type="text"
                                                        placeholder={`Enter ${targetTypeList.find(t => t.value === group.targetType)?.label}...`}
                                                        value={group.targetTypeValue || ''}
                                                        onChange={(e) => handleFilterGroupChange(groupIndex, 'targetTypeValue', e.target.value)}
                                                    />
                                                </div>
                                            ) : group.targetType && ['Severity', 'Valid'].includes(group.targetType) ? (
                                                <div className="filter-group-field">
                                                    <span className="filter-group-field-label">Value</span>
                                                    <div className={`filter-group-checkbox-group columns-checkbox-group columns-checkbox-group--filter ${group.targetType && !group.targetTypeValue ? 'has-error' : ''}`}>
                                                        {getTargetTypeItems(group.targetType).map(item => {
                                                            const selectedValues = (group.targetTypeValue || '').split(',').filter(Boolean);
                                                            const isChecked = selectedValues.includes(item.value);
                                                            return (
                                                                <label key={item.value} className="column-checkbox filter-checkbox-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => handleCheckboxToggle(groupIndex, group.targetTypeValue || '', item.value)}
                                                                    />
                                                                    <span>{item.label}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : group.targetType ? (
                                                (() => {
                                                    const targetTypeItems = getTargetTypeItems(group.targetType);
                                                    const isTargetTypeLoading = shouldShowTargetTypeLoader(group.targetType);
                                                    const hasNoTargetTypeItems = shouldShowTargetTypeEmptyState(group.targetType, targetTypeItems);
                                                    const isMultiValueFilter = supportsMultipleFilterValues(group.targetType);
                                                    const selectedValue = isMultiValueFilter
                                                        ? getSelectedFilterOptions(targetTypeItems, group.targetTypeValue)
                                                        : targetTypeItems.find(i => i.value === (group.targetTypeValue || '')) || { value: '', label: 'All' };
                                                    const selectOptions = isMultiValueFilter
                                                        ? targetTypeItems
                                                        : [{ value: '', label: 'All' }, ...targetTypeItems];

                                                    return (
                                                <div className="filter-group-field">
                                                    <span className="filter-group-field-label">Value</span>
                                                    <Select
                                                        inputId={`theme-filter-value-${section.clientId || section.id || index}-${groupIndex}`}
                                                        instanceId={`theme-filter-value-${section.clientId || section.id || index}-${groupIndex}`}
                                                        className={`oct_selectsearch-v2 theme-section-select ${isMultiValueFilter ? 'theme-section-select--multi' : ''}`}
                                                        classNamePrefix="oct-selectsearchsub-v2"
                                                        value={isTargetTypeLoading || hasNoTargetTypeItems ? null : selectedValue}
                                                        onChange={(option) => handleFilterGroupChange(groupIndex, 'targetTypeValue', getFilterValueFromSelectOption(option))}
                                                        options={isTargetTypeLoading || hasNoTargetTypeItems ? [] : selectOptions}
                                                        isMulti={isMultiValueFilter}
                                                        isLoading={isTargetTypeLoading}
                                                        isSearchable={true}
                                                        {...tableViolationSelectMenuProps}
                                                        placeholder={isTargetTypeLoading ? 'Loading values...' : hasNoTargetTypeItems ? 'No values available' : 'Select Value...'}
                                                        loadingMessage={() => 'Loading values...'}
                                                        noOptionsMessage={() => hasNoTargetTypeItems ? 'No values available' : 'No options'}
                                                    />
                                                </div>
                                                    );
                                                })()
                                            ) : (
                                                <div className="filter-group-field">
                                                    <span className="filter-group-field-label">Value</span>
                                                    <Select
                                                        inputId={`theme-filter-value-disabled-${section.clientId || section.id || index}-${groupIndex}`}
                                                        instanceId={`theme-filter-value-disabled-${section.clientId || section.id || index}-${groupIndex}`}
                                                        className="oct_selectsearch-v2 theme-section-select"
                                                        classNamePrefix="oct-selectsearchsub-v2"
                                                        isDisabled={true}
                                                        isSearchable={false}
                                                        placeholder="Select a property first"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* +/- Buttons */}
                                        <div className="filter-group-actions">
                                            {groupIndex === filterGroups.length - 1 && (
                                                <button
                                                    type="button"
                                                    className="filter-group-btn-add"
                                                    onClick={addFilterGroup}
                                                    title="Add Filter Group"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            )}
                                            {filterGroups.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="filter-group-btn-remove"
                                                    onClick={() => removeFilterGroup(groupIndex)}
                                                    title="Remove Filter Group"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'CUSTOM_HTML':
                return (
                    <div className="section-config-row column">
                        <div className="section-config-item full-width">
                            <label>Content</label>
                            <LexicalEditor
                                value={contentHtml}
                                onChange={handleHtmlChange}
                                placeholder="Write your custom content here..."
                            />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="theme-section-card">
            <div className="section-card-header">
                <div className="section-card-left">
                    <DragIndicatorIcon className="drag-handle" />
                    {getSectionIcon()}
                    <div className="section-card-title-group">
                        <input
                            type="text"
                            className="section-name-input"
                            value={sectionName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="Section Name"
                        />
                        {getCategoryBadge()}
                    </div>
                </div>
                <div className="section-card-actions">
                    <button
                        type="button"
                        className="section-action-btn"
                        onClick={() => onMove(-1)}
                        disabled={index === 0}
                        title="Move Up"
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </button>
                    <button
                        type="button"
                        className="section-action-btn"
                        onClick={() => onMove(1)}
                        disabled={index === totalSections - 1}
                        title="Move Down"
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </button>
                    <button
                        type="button"
                        className="section-action-btn delete"
                        onClick={onDelete}
                        title="Delete Section"
                    >
                        <DeleteIcon fontSize="small" />
                    </button>
                    <button
                        type="button"
                        className="section-action-btn"
                        onClick={() => setCollapsed(!collapsed)}
                        title={collapsed ? 'Expand' : 'Collapse'}
                    >
                        {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                    </button>
                </div>
            </div>
            {!collapsed && (
                <div className="section-card-body">
                    {renderConfigFields()}
                    {renderChartPreview()}
                </div>
            )}
        </div>
    );
};

export default ThemeSectionCard;
